import axios from 'axios';
import { AIGenerateContentRequest, AIGenerateContentResponse, AIMappingSuggestion } from '../types/ai';
import env from '../utils/env';
import { PlaybookTransformItem, PlaybookMapping } from '@/types/playbook';
import { Parameter } from '@/types/schema';

// Ensure apiUrl does not have a trailing slash before appending /api
const API_BASE_URL = `${env.apiUrl.endsWith('/') ? env.apiUrl.slice(0, -1) : env.apiUrl}/api`;

export interface PlaybookAIMappingResult {
  inputPath: string;
  normalizedPath: string;
  certaintyScore: number;
  isRegex?: boolean;
  regexPattern?: string;
  matchStatus?: 'matched' | 'no-match';
}

/**
 * Validates an AI mapping suggestion to ensure it has all required fields and proper types
 * @param suggestion The suggestion to validate
 * @returns An object with validation result and any error messages
 */
export const validatePlaybookAIMappingSuggestion = (suggestion: any): {
  isValid: boolean;
  errors: string[];
  suggestion?: PlaybookAIMappingResult;
} => {
  const errors: string[] = [];
  
  // Check for required fields and proper types
  if (typeof suggestion.inputPath !== 'string') {
    errors.push('inputPath must be a string');
  }
  
  if (typeof suggestion.normalizedPath !== 'string') {
    errors.push('normalizedPath must be a string');
  }
  
  if (typeof suggestion.certaintyScore !== 'number') {
    errors.push('certaintyScore must be a number');
  }
  
  if (suggestion.isRegex !== undefined && typeof suggestion.isRegex !== 'boolean') {
    errors.push('isRegex must be a boolean when provided');
  }
  
  if (suggestion.regexPattern !== undefined && typeof suggestion.regexPattern !== 'string') {
    errors.push('regexPattern must be a string when provided');
  }
  
  // If isRegex is true, regexPattern must be provided
  if (suggestion.isRegex === true && typeof suggestion.regexPattern !== 'string') {
    errors.push('regexPattern is required when isRegex is true');
  }

  // Add matchStatus if not present
  let validatedSuggestion: PlaybookAIMappingResult | undefined;
  
  if (errors.length === 0) {
    // Determine match status if not already provided
    const matchStatus = suggestion.matchStatus ||
      (!suggestion.normalizedPath || suggestion.normalizedPath === '' ? 'no-match' : 'matched');
    
    validatedSuggestion = {
      ...suggestion,
      matchStatus
    };
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    suggestion: validatedSuggestion
  };
};

/**
 * Gets an AI mapping suggestion for a single property in a Playbook
 * @param inputProperty The property to map
 * @param targetSchemaProperties The target schema properties to map to
 * @param signal Optional AbortSignal for cancellation
 * @returns The mapping result or null if no mapping was found
 */
export const getPlaybookAIMappingSuggestion = async (
  inputProperty: { name: string; path: string; type: string; },
  targetSchemaProperties: { name: string; path: string; type: string; }[],
  signal?: AbortSignal
): Promise<PlaybookAIMappingResult | null> => {
  try {
    const requestBody: AIGenerateContentRequest = {
      contentType: 'aiMapping',
      context: {
        inputProperties: [inputProperty],
        targetSchemaProperties: targetSchemaProperties,
      },
    };

    const response = await axios.post<AIGenerateContentResponse>(
      `${API_BASE_URL}/ai/generateContent`,
      requestBody,
      {
        signal: signal,
      }
    );
    
    if (response.data.aiMappingResponse && response.data.aiMappingResponse.suggestions.length > 0) {
      // The backend is expected to return suggestions sorted by certaintyScore descending
      const suggestion: AIMappingSuggestion = response.data.aiMappingResponse.suggestions[0];
      
      // Validate the suggestion on the frontend side
      const validationResult = validatePlaybookAIMappingSuggestion(suggestion);
      
      if (validationResult.isValid && validationResult.suggestion) {
        return validationResult.suggestion;
      } else {
        console.error('Invalid AI mapping suggestion:', validationResult.errors);
        throw new Error(`Invalid AI mapping response: ${validationResult.errors.join(', ')}`);
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching AI mapping suggestion:', error);
    if (axios.isAxiosError(error)) {
      if (error.code === 'ERR_CANCELED') {
        throw new Error('AI mapping request was cancelled');
      }
      if (error.response) {
        const status = error.response.status;
        if (status === 503) {
          throw new Error('AI service is currently unavailable');
        }
        if (status >= 500) {
          throw new Error('AI service encountered an internal error');
        }
        if (status === 400) {
          throw new Error('Invalid request data for AI mapping');
        }
      }
    }
    throw error;
  }
};

/**
 * Gets AI mapping suggestions for a batch of properties in a Playbook
 * @param unmappedProperties The properties to map
 * @param targetSchemaProperties The target schema properties to map to
 * @param onProgress Optional callback for progress updates
 * @param signal Optional AbortSignal for cancellation
 * @returns An object containing the mapping results and statistics
 */
export const getPlaybookAIMappingSuggestionsBatch = async (
  unmappedProperties: { id: string; name: string; path: string; type: string; }[],
  targetSchemaProperties: { name: string; path: string; type: string; }[],
  onProgress?: (progress: number, details?: { current: number; total: number; message?: string; suggestions?: PlaybookAIMappingResult[] }) => void,
  signal?: AbortSignal
): Promise<{
  suggestions: PlaybookAIMappingResult[];
  stats: {
    total: number;
    valid: number;
    invalid: number;
    matched: number;
    unmatched: number;
  };
}> => {
  const BATCH_SIZE = 15;
  const allSuggestions: PlaybookAIMappingResult[] = [];
  const totalProperties = unmappedProperties.length;
  let processedCount = 0;
  let validCount = 0;
  let invalidCount = 0;
  let matchedCount = 0;
  let unmatchedCount = 0;

  try {
    console.log('🚀 Starting Playbook AI mapping batch for', totalProperties, 'properties with batch size', BATCH_SIZE);
    
    onProgress?.(0, {
      current: 0,
      total: totalProperties,
      message: 'Starting AI mapping...'
    });

    for (let i = 0; i < totalProperties; i += BATCH_SIZE) {
      const batchInputProperties = unmappedProperties.slice(i, i + BATCH_SIZE);
      
      // Check for cancellation before each request
      if (signal?.aborted) {
        console.log('Playbook AI mapping batch cancelled by signal.');
        throw new Error('Playbook AI mapping batch was cancelled.');
      }

      const requestBody: AIGenerateContentRequest = {
        contentType: 'aiMapping',
        context: {
          inputProperties: batchInputProperties.map(p => ({ name: p.name, path: p.path, type: p.type })),
          targetSchemaProperties: targetSchemaProperties,
        },
      };

      try {
        console.log(`📤 Sending Playbook AI mapping request for batch ${Math.floor(i / BATCH_SIZE) + 1} (${batchInputProperties.length} properties)`);
        
        const response = await axios.post<AIGenerateContentResponse>(
          `${API_BASE_URL}/ai/generateContent`,
          requestBody,
          {
            signal: signal,
          }
        );
        
        if (response.data.aiMappingResponse && response.data.aiMappingResponse.suggestions.length > 0) {
          const rawSuggestions = response.data.aiMappingResponse.suggestions;
          
          for (const rawSuggestion of rawSuggestions) {
            const validationResult = validatePlaybookAIMappingSuggestion(rawSuggestion);
            
            if (validationResult.isValid && validationResult.suggestion) {
              allSuggestions.push(validationResult.suggestion);
              validCount++;
              if (validationResult.suggestion.matchStatus === 'matched') {
                matchedCount++;
              } else {
                unmatchedCount++;
              }
            } else {
              console.warn('Invalid Playbook AI mapping suggestion received in batch:', validationResult.errors, rawSuggestion);
              invalidCount++;
              // Add a placeholder for invalid suggestions to keep total count accurate
              // Find the original property that this invalid suggestion was meant for
              const originalProperty = batchInputProperties.find(p => p.path === rawSuggestion.inputPath);
              if (originalProperty) {
                allSuggestions.push({
                  inputPath: originalProperty.path,
                  normalizedPath: '', // No match
                  certaintyScore: 0,
                  matchStatus: 'no-match',
                });
              }
            }
          }
        } else {
          console.warn('No valid Playbook AI mapping suggestions received for batch:', batchInputProperties.map(p => p.path), response.data);
          // Mark all properties in this batch as invalid/unmatched if no suggestions were returned
          invalidCount += batchInputProperties.length;
          batchInputProperties.forEach(p => {
            allSuggestions.push({
              inputPath: p.path,
              normalizedPath: '', // No match
              certaintyScore: 0,
              matchStatus: 'no-match',
            });
          });
        }
      } catch (innerError) {
        console.error(`Error fetching Playbook AI mapping suggestion for batch starting with ${batchInputProperties[0]?.path}:`, innerError);
        invalidCount += batchInputProperties.length;
        batchInputProperties.forEach(p => {
          allSuggestions.push({
            inputPath: p.path,
            normalizedPath: '', // No match due to error
            certaintyScore: 0,
            matchStatus: 'no-match',
          });
        });
        // Decide if you want to re-throw or continue on individual errors
        // For now, we'll log and continue to process other batches
        if (axios.isAxiosError(innerError) && innerError.code === 'ERR_CANCELED') {
          throw new Error('Playbook AI mapping batch was cancelled during property processing.');
        }
      }
      
      processedCount += batchInputProperties.length;
      const currentProgress = (processedCount / totalProperties) * 100;
      onProgress?.(currentProgress, {
        current: processedCount,
        total: totalProperties,
        message: `Processed ${processedCount} of ${totalProperties} properties`,
        suggestions: allSuggestions // Pass accumulated suggestions
      });
    }
    
    console.log('✅ Playbook AI mapping batch completed.');
    
    return {
      suggestions: allSuggestions,
      stats: {
        total: totalProperties,
        valid: validCount,
        invalid: invalidCount,
        matched: matchedCount,
        unmatched: unmatchedCount
      }
    };
  } catch (error) {
    console.error('Error during Playbook AI mapping batch processing:', error);
    onProgress?.(0, {
      current: 0,
      total: totalProperties,
      message: 'Error occurred during AI mapping'
    });
    
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw new Error('Playbook AI mapping batch request was cancelled');
    }
    throw error;
  }
};

/**
 * Attempts to retry failed AI mapping suggestions for Playbooks
 * @param failedProperties The properties that failed in the previous attempt
 * @param targetSchemaProperties The target schema properties to map to
 * @param onProgress Optional callback for progress updates
 * @param signal Optional AbortSignal for cancellation
 * @returns The mapping results for the retried properties
 */
export const retryFailedPlaybookMappings = async (
  failedProperties: { id: string; name: string; path: string; type: string; }[],
  targetSchemaProperties: { name: string; path: string; type: string; }[],
  onProgress?: (progress: number, details?: { current: number; total: number; message?: string }) => void,
  signal?: AbortSignal
): Promise<{
  suggestions: PlaybookAIMappingResult[];
  stats: {
    total: number;
    valid: number;
    invalid: number;
    matched: number;
    unmatched: number;
  };
}> => {
  console.log(`Retrying Playbook AI mapping for ${failedProperties.length} failed properties`);
  return getPlaybookAIMappingSuggestionsBatch(
    failedProperties,
    targetSchemaProperties,
    onProgress,
    signal
  );
};

/**
 * Automatically maps properties with exact name matches
 * @param inputProperties The input properties to map
 * @param outputProperties The output properties to map to
 * @returns An array of mapping results
 */
export const autoMapPlaybookProperties = (
  inputProperties: Parameter[],
  outputProperties: Parameter[]
): PlaybookAIMappingResult[] => {
  console.log('autoMapPlaybookProperties: Input Properties:', inputProperties.map(p => ({ path: p.path, codeName: p.codeName })));
  console.log('autoMapPlaybookProperties: Output Properties:', outputProperties.map(p => ({ path: p.path, codeName: p.codeName })));

  const results: PlaybookAIMappingResult[] = [];
  
  // Create maps for output properties for efficient lookup
  const outputPathMap = new Map<string, Parameter>();
  const outputCodeNameMap = new Map<string, Parameter>();

  outputProperties.forEach(prop => {
    const lowerCasePath = prop.path.toLowerCase();
    const lowerCaseCodeName = prop.codeName.toLowerCase();
    const pathSegments = prop.path.split('.');

    // Store by full path
    if (!outputPathMap.has(lowerCasePath)) {
      outputPathMap.set(lowerCasePath, prop);
    }

    // Store by codeName, prioritizing less nested paths
    const existingPropWithCodeName = outputCodeNameMap.get(lowerCaseCodeName);
    if (!existingPropWithCodeName || pathSegments.length < existingPropWithCodeName.path.split('.').length) {
      outputCodeNameMap.set(lowerCaseCodeName, prop);
    }
  });
  
  inputProperties.forEach(inputProp => {
    const lowerCaseInputPath = inputProp.path.toLowerCase();
    const lowerCaseInputCodeName = inputProp.codeName.toLowerCase();
    
    let matchedOutputProp: Parameter | undefined;

    // 1. Try exact full path match
    matchedOutputProp = outputPathMap.get(lowerCaseInputPath);
    if (matchedOutputProp) {
    }

    // 2. If no full path match, try exact codeName match
    if (!matchedOutputProp) {
      const candidateProp = outputCodeNameMap.get(lowerCaseInputCodeName);
      if (candidateProp) {
        matchedOutputProp = candidateProp;
      }
    }
    
    if (matchedOutputProp) {
      results.push({
        inputPath: inputProp.path,
        normalizedPath: matchedOutputProp.path,
        certaintyScore: 1.0, // Exact matches get a perfect score
        matchStatus: 'matched'
      });
    } else {
      results.push({
        inputPath: inputProp.path,
        normalizedPath: '',
        certaintyScore: 0,
        matchStatus: 'no-match'
      });
    }
  });
  
  console.log('autoMapPlaybookProperties: Final results:', results);
  return results;
};

/**
 * Creates PlaybookTransformItem objects from mapping results
 * @param mappingResults The mapping results to convert
 * @param inputSchema Optional input schema for type lookup
 * @param outputSchema Optional output schema for type lookup
 * @returns An array of PlaybookTransformItem objects
 */
export const createTransformsFromMappingResults = (
  mappingResults: PlaybookAIMappingResult[],
  inputSchema?: any,
  outputSchema?: any
): PlaybookTransformItem[] => {
  return mappingResults
    .map(result => {
      // Helper function to find parameter data type in schema
      const findParameterDataType = (schema: any, path: string): string | undefined => {
        if (!schema || !path) return undefined;
        const param = schema.parameters?.find((p: any) => p.fullPath === path || p.path === path);
        return param?.dataTypes?.[0];
      };

      const inputType = findParameterDataType(inputSchema, result.inputPath);
      const outputType = findParameterDataType(outputSchema, result.normalizedPath);

      let appliesToScope: PlaybookTransformItem['appliesToScope'] = 'global';
      let eventIds: string[] | undefined = undefined;

      // Check if the output property belongs to any event in the output schema
      if (outputSchema && outputSchema.events) {
        for (const event of outputSchema.events) {
          if (event.parameterFullPaths && event.parameterFullPaths.includes(result.normalizedPath)) {
            appliesToScope = 'eventSpecific';
            eventIds = [event.id];
            break; // Found the event, no need to check other events
          }
        }
      }

      const transform: PlaybookTransformItem = {
        id: `transform_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'mapping',
        content: {
          inputKey: result.inputPath,
          outputKey: result.normalizedPath,
          transforms: []
        } as PlaybookMapping,
        appliesToScope: appliesToScope, // Dynamically set scope
        eventIds: eventIds, // Dynamically set event IDs
        inputPath: result.inputPath,
        outputPath: result.normalizedPath,
        inputType: inputType, // Set input type for data type icon
        outputType: outputType, // Set output type for data type icon
        mappingType: result.isRegex ? 'regex' : 'ai', // Use 'ai' instead of 'direct' for AI-generated mappings
        __meta: {
          certaintyScore: result.certaintyScore
        }
      };
      
      return transform;
    });
};
